import { Component, EventEmitter, Output, OnInit } from '@angular/core';
import { StaffService } from '../../../services/staff.service';
import { StaffMember } from '../../../models/StaffMember';

export interface MechanicSelection {
  id: number;
  nome: string;
  cargo: string;
  especialidade: string;
  telefone: string;
  email: string;
}

@Component({
  selector: 'app-mechanic-selection-modal',
  templateUrl: './mechanic-selection-modal.component.html',
  styleUrls: ['./mechanic-selection-modal.component.css']
})
export class MechanicSelectionModalComponent implements OnInit {
  @Output() mechanicSelected = new EventEmitter<MechanicSelection>();
  @Output() closeModal = new EventEmitter<void>();

  showModal: boolean = false;
  searchTerm: string = '';
  mechanics: MechanicSelection[] = [];
  filteredMechanics: MechanicSelection[] = [];
  selectedMechanic: MechanicSelection | null = null;
  isLoading: boolean = false;
  
  // Paginação
  currentPage: number = 1;
  pageSize: number = 10;
  totalCount: number = 0;
  totalPages: number = 0;

  constructor(private staffService: StaffService) { }

  ngOnInit(): void {
    this.loadStaff();
  }

  /**
   * Helper para usar Math.min no template
   */
  get Math() {
    return Math;
  }

  /**
   * Carrega funcionários do serviço
   */
  loadStaff(): void {
    this.isLoading = true;
    
    this.staffService.getStaffPaginated({
      page: this.currentPage,
      pageSize: this.pageSize,
      search: this.searchTerm || undefined,
      sortBy: 'FullName',
      sortDirection: 'asc'
    }).subscribe({
      next: (response) => {
        this.totalCount = response.totalCount;
        this.totalPages = response.totalPages;
        this.currentPage = response.page;
        this.mechanics = this.mapStaffToMechanics(response.staff);
        this.filteredMechanics = [...this.mechanics];
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Erro ao carregar funcionários:', error);
        this.isLoading = false;
      }
    });
  }

  /**
   * Mapeia StaffMember para MechanicSelection
   */
  private mapStaffToMechanics(staff: StaffMember[]): MechanicSelection[] {
    return staff.map(s => ({
      id: s.Id,
      nome: s.FullName,
      cargo: s.Position || 'Não informado',
      especialidade: s.Department || 'Não informado',
      telefone: this.extractPhone(s.Contacts),
      email: this.extractEmail(s.Contacts)
    }));
  }

  /**
   * Extrai telefone dos contatos
   */
  private extractPhone(contacts: any[]): string {
    if (!contacts || contacts.length === 0) return 'Não informado';
    const contact = contacts.find(c => c.Type === 'Telefone' || c.Type === 'Celular') || contacts[0];
    return contact?.Value || 'Não informado';
  }

  /**
   * Extrai email dos contatos
   */
  private extractEmail(contacts: any[]): string {
    if (!contacts || contacts.length === 0) return 'Não informado';
    const contact = contacts.find(c => c.Type === 'Email') || contacts[0];
    return contact?.Value || 'Não informado';
  }

  open(): void {
    this.showModal = true;
    this.searchTerm = '';
    this.selectedMechanic = null;
    this.currentPage = 1;
    this.loadStaff();
  }

  close(): void {
    this.showModal = false;
    this.closeModal.emit();
  }

  onSearch(): void {
    // Recarrega com o termo de busca
    this.currentPage = 1;
    this.loadStaff();
  }

  selectMechanic(mechanic: MechanicSelection): void {
    this.selectedMechanic = mechanic;
  }

  confirmSelection(): void {
    if (this.selectedMechanic) {
      this.mechanicSelected.emit(this.selectedMechanic);
      this.close();
    }
  }

  /**
   * Vai para a próxima página
   */
  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.selectedMechanic = null;
      this.loadStaff();
    }
  }

  /**
   * Vai para a página anterior
   */
  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.selectedMechanic = null;
      this.loadStaff();
    }
  }

  /**
   * Vai para uma página específica
   */
  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.selectedMechanic = null;
      this.loadStaff();
    }
  }

  /**
   * Gera array de números de página para exibição
   */
  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxPagesToShow = 5;
    
    if (this.totalPages <= maxPagesToShow) {
      // Mostra todas as páginas se forem poucas
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Lógica para mostrar páginas com reticências
      const halfWindow = Math.floor(maxPagesToShow / 2);
      let startPage = Math.max(1, this.currentPage - halfWindow);
      let endPage = Math.min(this.totalPages, this.currentPage + halfWindow);
      
      // Ajusta se estiver no início
      if (this.currentPage <= halfWindow) {
        endPage = maxPagesToShow;
      }
      
      // Ajusta se estiver no final
      if (this.currentPage >= this.totalPages - halfWindow) {
        startPage = this.totalPages - maxPagesToShow + 1;
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  }
}
